import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Plus, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', domain: '', plan_type: 'free', admin_email: '', admin_password: '', admin_name: '' });
  const navigate = useNavigate();

  const load = () => {
    api.get('/tenants')
      .then((r) => { setTenants(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      await api.post('/tenants', form);
      toast.success(res.data?.admin_created ? `Bayi ve yönetici (${res.data.admin_created.email}) oluşturuldu` : 'Bayi oluşturuldu');
      setShowCreate(false);
      setForm({ name: '', domain: '', plan_type: 'free', admin_email: '', admin_password: '', admin_name: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Loading tenants...</div>;

  return (
    <div data-testid="tenants-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Bayiler</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">{tenants.length} toplam bayi</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-tenant-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] transition-transform duration-100">
          <Plus size={16} className="mr-2" /> Yeni Bayi
        </Button>
      </div>

      <Card className="bg-[#141414] border-[#262626]">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-[#A3A3A3] font-ibm">Ad</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Alan Adı</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Plan</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Durum</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm text-right">Oluşturulma</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.map((t) => (
              <TableRow
                key={t.id}
                data-testid={`tenant-row-${t.id}`}
                className="border-[#262626] cursor-pointer hover:bg-white/5 transition-colors duration-200"
                onClick={() => navigate(`/tenants/${t.id}`)}
              >
                <TableCell className="font-ibm text-white font-medium">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-[#FACC15]" />
                    {t.name}
                  </div>
                </TableCell>
                <TableCell className="text-[#A3A3A3] font-ibm">{t.domain || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 capitalize">{t.plan_type}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={t.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                    {t.is_active ? 'Aktif' : 'Devre Dışı'}
                  </Badge>
                </TableCell>
                <TableCell className="text-[#737373] font-jetbrains text-xs text-right">
                  {new Date(t.created_at).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">Bayi Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Ad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Acme Inc" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Alan Adı</Label>
              <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="tenant-domain-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="acme.com" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Plan</Label>
              <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="tenant-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator className="bg-[#262626]" />
            <p className="text-xs text-[#737373] font-ibm">Bayi Yönetici Hesabı (opsiyonel)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Yönetici Adı</Label>
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="admin-name-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Ahmet Yılmaz" />
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Yönetici E-posta</Label>
                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="admin-email-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="admin@bayi.com" />
              </div>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Yönetici Şifre</Label>
              <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="admin-password-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="En az 6 karakter" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleCreate} data-testid="create-tenant-submit" className="bg-white text-black hover:bg-[#E5E5E5]">Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
